export type ZonedParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(timeZone: string) {
  let formatter = formatterCache.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    formatterCache.set(timeZone, formatter);
  }
  return formatter;
}

export function getZonedParts(instant: Date, timeZone: string): ZonedParts {
  const values: Record<string, string> = {};
  partsFormatter(timeZone)
    .formatToParts(instant)
    .forEach((part) => {
      if (part.type !== "literal") values[part.type] = part.value;
    });

  return {
    year: Number(values.year),
    month: Number(values.month),
    day: Number(values.day),
    hour: Number(values.hour),
    minute: Number(values.minute),
    second: Number(values.second),
  };
}

export function getOffsetSeconds(instant: Date, timeZone: string) {
  const parts = getZonedParts(instant, timeZone);
  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );
  return Math.round((asUtc - instant.getTime()) / 60_000) * 60;
}

export function zonedDateTimeToInstant(parts: Omit<ZonedParts, "second">, timeZone: string) {
  const wanted = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, 0);
  let guess = wanted;

  for (let attempt = 0; attempt < 4; attempt += 1) {
    const seen = getZonedParts(new Date(guess), timeZone);
    const seenAsUtc = Date.UTC(seen.year, seen.month - 1, seen.day, seen.hour, seen.minute, 0);
    const correction = wanted - seenAsUtc;
    guess += correction;
    if (correction === 0) break;
  }

  return new Date(guess);
}

export function instantAtMinuteOfDay(reference: Date, timeZone: string, minuteOfDay: number) {
  const local = getZonedParts(reference, timeZone);
  const safeMinute = Math.min(1439, Math.max(0, Math.round(minuteOfDay)));
  return zonedDateTimeToInstant(
    {
      year: local.year,
      month: local.month,
      day: local.day,
      hour: Math.floor(safeMinute / 60),
      minute: safeMinute % 60,
    },
    timeZone,
  );
}

export function minuteOfDay(instant: Date, timeZone: string) {
  const parts = getZonedParts(instant, timeZone);
  return parts.hour * 60 + parts.minute;
}

export function formatTime(instant: Date, timeZone: string, timeFormat: 12 | 24) {
  const formatted = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: timeFormat === 24 ? "h23" : "h12",
  }).format(instant);

  if (timeFormat === 24) return { time: formatted, period: "" };
  const [time, period = ""] = formatted.split(" ");
  return { time, period };
}

export function formatDate(instant: Date, timeZone: string) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    day: "numeric",
    month: "short",
  }).format(instant);
}

export function formatDayKey(instant: Date, timeZone: string) {
  const parts = getZonedParts(instant, timeZone);
  return `${parts.year}-${parts.month}-${parts.day}`;
}

export function formatOffsetName(instant: Date, timeZone: string) {
  const part = new Intl.DateTimeFormat("en-US", {
    timeZone,
    timeZoneName: "shortOffset",
  })
    .formatToParts(instant)
    .find(({ type }) => type === "timeZoneName")?.value;
  return part?.replace("GMT", "GMT") ?? timeZone;
}

export function formatDifference(seconds: number) {
  if (seconds === 0) return "";
  const hours = seconds / 3600;
  const absolute = Number.isInteger(hours) ? String(Math.abs(hours)) : String(Math.abs(hours).toFixed(1));
  return `${hours > 0 ? "+" : "−"}${absolute}H`;
}
